import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject, tap } from 'rxjs';
import { AppConstants } from 'src/app/constants/app-constants';
import { UserService } from '../user/user.service';
import { User } from 'src/app/model/User';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import { AbstractApiService } from '../abstract-api-service/abstract-api-service';
import { HttpClient } from '@angular/common/http';
import { Person, PersonDto, PersonMapper } from 'src/app/model/Person';
import { USER_DEACTIVATE_ENDPOINT, COMPANY_PEOPLE_ENDPOINT, USER_REACTIVATE_ENDPOINT, INVITE_MEMBER_WITH_GROUP_ENDPOINT } from 'src/app/constants/api-routes';
import { UserInviteInterface, UserInviteMapper } from 'src/app/model/UserInvite';

/**
 * PeopleService is used to hold/move user data from the backend to the application
 */
@Injectable({
  providedIn: 'root'
})
export class PeopleService extends AbstractApiService<Person> {
  /** Observable that emits the latest list of people */
  protected _subject = new Subject<Person[]>();
  public changes$ = this._subject.asObservable();

  /** A list of 'people' the user has access to */
  protected _collection?: Person[];

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>("0:00");
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the service, used to initialize services
   *
   * @param http
   * @param authService
   */
  constructor(
    private _userService: UserService,
    private _authService: AuthService,
    protected _http: HttpClient,
  ) {
    super();
    // On authentication changes, clear persisted data
    this._userService.user$.subscribe((user: User | null) => {
      if (this._authService.authenticated && user != undefined) {
        this._fetch();
      } else {
        this._collection = undefined;
        this._subject.next([]);
      }
    });

    // Continuously query the backend to receive new applications
    this._startTimer(0);
    this.timer.subscribe(
      (x: number) => {
        this._timers.splice(this._timers.indexOf(x));
        this._startTimer(x + 1);
        this._fetch();
      });
  }

  /**
   * Get a list of people
   *
   * @return A reference to the current collection
   */
  public getEntities(): Person[] | undefined {
    return this._collection?.map(x => x.copyOf());
  }

  /**
   * Set a list of people to the current collection
   *
   * @param people
   */
  private _setEntities(people: Person[]): void {
    this._collection = people.map(x => x.copyOf());
    this._subject.next(people.map(x => x.copyOf()));
  }

  /**
   * On a group update, refresh the people list
   */
  public onGroupUpdate(personId: string, groupName: string): void {
    // Find person by id
    const person = this._collection?.find(p => p.id === personId);
    if (person == undefined) return;

    // Update
    person.group = groupName;
    this.refresh(person, 'modify');
  }

  /**
   * Deactivate a user by setting their status to 'deactivated' and hitting a backend endpoint
   * 
   * @param personId The ID of the person to deactivate
   */
  public deactivatePerson(personId: string): Observable<any> {
    // Find person by id
    const person = this._collection?.find(p => p.id === personId);
    if (person == undefined) {
      throw new Error(`Person with ID ${personId} not found`);
    }

    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('Company information is missing');
    }

    // Send the update to the backend
    return this._http.put<{message: string, status: string}>(USER_DEACTIVATE_ENDPOINT(companyId, personId), {}).pipe(
      tap({
        next: (resp: {message: string, status: string}) => {
          console.log(`[INFO]: successfully deactivated person with ID ${personId}`, resp);

          // Deactivate the person in the local collection
          person.status = 'deactivated';
          this.refresh(person, 'modify');
        },
        error: (err) => {
          console.error(`[ERR]: failed to deactivate person with ID ${personId}`, err);
        }
      })
    );
  }

  /**
   * Reactivate a user by setting their status to 'active' and hitting a backend endpoint
   * 
   * @param personId The ID of the person to deactivate
   */
  public reactivatePerson(personId: string): Observable<any> {
    // Find person by id
    const person = this._collection?.find(p => p.id === personId);
    if (person == undefined) {
      throw new Error(`Person with ID ${personId} not found`);
    }

    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('Company information is missing');
    }

    // Send the update to the backend
    return this._http.put<{message: string, status: string}>(USER_REACTIVATE_ENDPOINT(companyId, personId), {}).pipe(
      tap({
        next: (resp: {message: string, status: string}) => {
          console.log(`[INFO]: successfully reactivated person with ID ${personId}`, resp);

          // Reactivate the person in the local collection
          person.status = 'active';
          this.refresh(person, 'modify');
        },
        error: (err) => {
          console.error(`[ERR]: failed to reactivate person with ID ${personId}`, err);
        }
      })
    );
  }

  /**
   * Invites a new member and persists their invite record
   * 
   * @note All groups are currently ignored and set to 'Admin' by default
   */
  inviteMemberWithGroup(member: {
    email: string;
    firstName: string;
    lastName: string;
    //group: string;
  }): Observable<UserInviteInterface> {
    // Find company ID
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) throw new Error("Company ID is undefined");

    // Build payload including the group
    const payload = {
      email:     member.email,
      firstName: member.firstName,
      lastName:  member.lastName,
      //group:     member.group
      group:     'Admin' // Default group as per current implementation
    };

    return this._http.post<UserInviteInterface>(INVITE_MEMBER_WITH_GROUP_ENDPOINT(companyId), payload).pipe(
      tap((inviteRecord: UserInviteInterface) => {
        // Add the new invite to the local collection
        const person = UserInviteMapper.toPerson(inviteRecord);
        this.refresh(person, 'create');
      }),
    )
  }

  /**
   * Get people from the backend. You should subscribe to the 'people$'
   * field to receive updates
   */
  private _fetch(): void {
    // Make sure the user is authenticated
    if (!this._authService.authenticated) return;

    // Get the user's active company
    const company = this._userService.company;
    if (company == undefined) return;

    this._http.get<PersonDto[]>(COMPANY_PEOPLE_ENDPOINT(company.companyId)).subscribe({
      next: (personInterfaces: PersonDto[]): void => {
        if (personInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into person list`);
        }

        // Convert the interfaces to class values
        let people: Person[] = [];
        for (let personInt of personInterfaces) {
          people.push(PersonMapper.toModel(personInt));
        }

        // Propagate changes if different
        this._setEntities(people);
        console.log(`[INFO]: successfully queried people`);
      },
    });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(`${minutes}:${(""+seconds).length === 1 ? "0" : ""}${seconds}`);
    }
  }
}

// Helper function to delay for a given number of milliseconds
const delay = (delay: number): Promise<void> => {
  return new Promise(r => {setTimeout(r, delay)});
}
